import React, { FC } from 'react';

import { Box, keyframes } from '@mui/material';
import Image from 'next/image';
import VestaLogoV2 from 'public/v2/icons/vstaMainLogo.svg';

import { primaryColors } from '~components/theme/styles/colors';
import { ENativeAsset } from '~config/types';

import { AssetIcon } from '../icons/assets';
import { IconColorWrapper } from '../icons/iconColorWrapper';

export const ImageVestaLogoV2: FC<{
  height?: number;
  width?: number;
  background: string;
}> = ({ height, width, background }) => (
  <IconColorWrapper color={background} sx={{ height: height ? `${height}px` : '50px' }}>
    <VestaLogoV2 width={width ? `${width}px` : '50px'} height={height ? `${height}px` : '50px'} />
  </IconColorWrapper>
);

export const RiskDAOLogoV2: FC<{ small?: boolean; height?: number; width?: number; background: string }> = ({
  small,
  height = 64,
  width = 304,
  background,
}) => (
  <Box
    sx={{
      background,
      mask: 'url(/v2/partners/riskdao-logo-text.svg) no-repeat center',
      WebkitMaskSize: '100% 100%',
      transition: 'background 0.25s ease',
    }}
    width={small ? width / 2 : width}
    height={small ? height / 2 : height}
  />
);

export const VerilogLogoV2: FC<{ small?: boolean; height?: number; width?: number; background: string }> = ({
  small,
  height = 32,
  width = 150,
  background,
}) => (
  <Box
    sx={{
      background,
      mask: 'url(/v2/partners/verilog-logo-text.svg) no-repeat center',
      WebkitMaskSize: '100% 100%',
      transition: 'background 0.25s ease',
    }}
    width={small ? width / 2 : width}
    height={small ? height / 2 : height}
  />
);

export const ImmuneFiLogoV2: FC<{ small?: boolean; height?: number; width?: number; background: string }> = ({
  small,
  height = 32,
  width = 150,
  background,
}) => (
  <Box
    sx={{
      background,
      mask: 'url(/v2/partners/immune.svg) no-repeat center',
      WebkitMaskSize: '100% 100%',
      transition: 'background 0.25s ease',
    }}
    width={small ? width / 2 : width}
    height={small ? height / 2 : height}
  />
);

const RotateAnimation = keyframes`
from {
  transform: rotate(0deg);
}
to {
  transform: rotate(360deg);
}
`;

const VST_TOKEN_RING_PATH = '/v2/graphics/vst-token-ring.svg';
const VSTA_TOKEN_RING_PATH = '/v2/graphics/vesta-token-ring.svg';

export const NativeTokenOrbital: FC<{
  small?: boolean;
  height?: number;
  width?: number;
  asset: ENativeAsset;
}> = ({ asset, small, height = 256, width = 256 }) => (
  <Box sx={{ pointerEvents: 'none', inWidth: width, minHeight: height, maxHeight: height, maxWidth: width }}>
    <Image width={width} height={height} src={'/v2/graphics/vesta-token.svg'} />
    <Box sx={{ position: 'relative', top: -208, left: 60 }}>
      <AssetIcon asset={asset} noBg noBorder size={140} />
    </Box>
    <Box
      sx={{
        top: -504,
        left: -100,
        position: 'relative',
        animation: `${RotateAnimation} 24s linear infinite`,
        background: primaryColors[400],
        mask: `url(${
          asset === ENativeAsset.VST ? VST_TOKEN_RING_PATH : VSTA_TOKEN_RING_PATH
        }) no-repeat center`,
        WebkitMaskSize: '100% 100%',
        transition: 'background 0.25s ease',
      }}
      width={small ? width / 2 : 460}
      height={small ? height / 2 : 460}
      minWidth={small ? width / 2 : 460}
      maxWidth={small ? height / 2 : 460}
    />
  </Box>
);

export const AestheticWaves: FC<{ height?: number; width?: number }> = ({ height = 515, width = 640 }) => (
  <Image width={width} height={height} src={'/v2/graphics/circular-waves.svg'} />
);

export const Wormhole: FC<{ height?: number; width?: number }> = ({ height = 384, width = 384 }) => (
  <Image width={width} height={height} src={'/v2/graphics/wormhole.svg'} />
);

export const GovernanceConstellation: FC<{ height?: number; width?: number }> = ({
  height = 606,
  width = 390,
}) => <Image width={width} height={height} src={'/v2/graphics/governance-constellation.svg'} />;

export const SafetyVault: FC<{ height?: number; width?: number }> = ({ height = 420, width = 403 }) => (
  <Image width={width} height={height} layout={'fill'} src={'/v2/graphics/safety-module.png'} />
);

export const PerspectiveGrid: FC<{ height?: number; width?: number }> = ({ height = 325, width = 744 }) => (
  <Image width={width} height={height} layout={'fill'} src={'/v2/graphics/safety-perpective-grid.svg'} />
);
export const PerspectiveGridFull: FC<{ height?: number; width?: number }> = ({
  height = 256,
  width = 2079,
}) => <Image width={width} height={height} layout={'fill'} src={'/v2/graphics/perspective-grid-full.svg'} />;
